import { TYPE_NAMES } from '../constants.js';
import { escapeHtml } from '../utils.js';
import { getPanelByTeam } from './panels.js';
import { resolveSummonDisplay } from './summonUtils.js';
import { createGroupedStatusBadgesHtml, createStatusBadgeHtml, enhanceStatusPreset } from './effectPresets.js';
import { getCurrentTurnAndAction } from './timeline.js';

export function renderBattlePlaybackPanels(state) {
  const container = document.getElementById('battle-playback-panels');
  if (!container) return;
  const timeline = state.timeline;
  const step = timeline.length ? Math.min(Math.max(state.currentStep, 0), timeline.length - 1) : 0;
  const snapshot = timeline[step]?.snapshot || [];
  const snapshotMap = new Map(snapshot.map(item => [item.id, item]));
  const allPanels = state.panels.slice().sort((a, b) => a.team.localeCompare(b.team));
  
  // Этап 3: Определяем текущий ход и действие для динамического отображения
  const { turn, action } = getCurrentTurnAndAction(timeline, step, allPanels);
  
  // Показываем все панели (обе команды)
  // Ранее была фильтрация, но по требованию пользователя показываем все карточки
  let visiblePanels = allPanels;
  
  container.innerHTML = visiblePanels
    .map(panel => {
      const hpInfo = snapshotMap.get(panel.id) || { hp: panel.maxHp, maxHp: panel.maxHp, energy: 100, maxEnergy: 100 };
      const percent = Math.max(0, Math.min(100, Math.round((hpInfo.hp / panel.maxHp) * 100)));
      const energyInfo = hpInfo.energy != null ? hpInfo.energy : (panel.raw?.state?.current?.energy ?? 100);
      const maxEnergyInfo = hpInfo.maxEnergy != null ? hpInfo.maxEnergy : (panel.raw?.state?.current?.maxEnergy ?? panel.raw?.state?.base?.maxEnergy ?? 100);
      const energyPercent = Math.max(0, Math.min(100, Math.round((energyInfo / maxEnergyInfo) * 100)));
      const avatarHtml = panel.avatar
        ? `<img src="${escapeHtml(panel.avatar)}" alt="" onerror="this.onerror=null;this.src='/creatures/1.png';">`
        : `<div class="battle-combatant-avatar">${escapeHtml(panel.name.charAt(0).toUpperCase())}</div>`;
      const typeTags = panel.types && panel.types.length
        ? `<div class="battle-ability-tags">${panel.types
            .map(type => `<span class="battle-ability-tag">${escapeHtml(TYPE_NAMES[type] || type)}</span>`)
            .join('')}</div>`
        : '';
      const statusList = (state.statusTimeline?.[step]?.statuses?.[String(panel.id)]) || [];
      const summonDisplay = resolveSummonDisplay(panel, state.panels);
      const summonHtml = summonDisplay
        ? `<div class="battle-summon-info"><span class="battle-summon-badge" title="${escapeHtml(summonDisplay.tooltip)}">${escapeHtml(summonDisplay.badgeLabel)}</span>${summonDisplay.ownerName ? `<span class="battle-summon-owner">${escapeHtml(summonDisplay.ownerName)}</span>` : ''}</div>`
        : '';
      // Используем улучшенную систему пресетов (Stage 4)
      const statusHtml = statusList.length
        ? `<div class="battle-status-badges">${createGroupedStatusBadgesHtml(statusList, {
            showTooltip: true,
            showStack: true,
            showDuration: false,
            currentRound: state.currentRound || null
          })}</div>`
        : '';
      // Улучшенное отображение описания способности
      const highlightText = panel.highlight
        ? `
            <div class="battle-ability-description" style="margin-top: 8px; padding: 8px; background: rgba(59, 130, 246, 0.08); border-radius: 6px; border-left: 3px solid rgba(59, 130, 246, 0.3);">
                ${panel.highlightTitle ? `<div style="font-weight: 600; font-size: 13px; margin-bottom: 4px; color: var(--tg-theme-text-color);">${escapeHtml(panel.highlightTitle)}</div>` : ''}
                <div style="font-size: 12px; line-height: 1.5; color: rgba(226,232,240,0.9);">
                    ${escapeHtml(panel.highlight).replace(/\n/g, '<br>')}
                </div>
            </div>
        `
        : '';
      
      // Определяем класс для низкого HP
      const lowHpClass = percent <= 30 ? 'battle-combatant-panel--low-hp' : '';
      const criticalHpClass = percent <= 15 ? 'battle-combatant-panel--critical-hp' : '';
      
      // Этап 3: Добавляем классы для атакующих/защищающихся карт
      let actionClass = '';
      if (action) {
        if (String(panel.id) === action.attacker) {
          actionClass = 'battle-card-attacking';
        } else if (String(panel.id) === action.defender) {
          actionClass = 'battle-card-defending';
        }
      }
      
      return `
          <div class="battle-combatant-panel battle-combatant-panel--team-${panel.team.toLowerCase()} ${lowHpClass} ${criticalHpClass} ${actionClass}" data-panel-id="${panel.id}" style="transition: all 0.3s ease;">
              <div class="battle-combatant-header">
                  ${avatarHtml}
                  <div class="battle-combatant-meta" style="flex: 1;">
                      <div class="battle-combatant-name" style="font-size: 16px; font-weight: 700; color: var(--tg-theme-text-color); margin-bottom: 2px;">${escapeHtml(panel.name)}</div>
                      <div class="battle-combatant-subtitle" style="font-size: 12px; color: var(--tg-theme-hint-color);">${escapeHtml(panel.subtitle || '')}</div>
                      ${summonHtml}
                  </div>
              </div>
              ${typeTags}
              ${statusHtml}
              <div class="battle-hp-track">
                  <div class="battle-hp-row">
                      <span>❤️ HP</span>
                      <div class="battle-hp-bar" style="background: rgba(255,255,255,0.1); border-radius: 4px; height: 8px; width: 100%; position: relative; overflow: hidden;">
                          <div class="battle-hp-fill" style="width:${percent}%; background: ${percent > 60 ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' : percent > 30 ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'}; height: 100%; transition: width 0.5s ease, background 0.3s ease; border-radius: 4px;"></div>
                      </div>
                      <span style="font-weight:600; font-size: 0.9em; min-width: 50px; text-align: right;">${hpInfo.hp}/${panel.maxHp}</span>
                  </div>
                  <div class="battle-energy-row" style="margin-top: 6px;">
                      <span>⚡ Энергия</span>
                      <div class="battle-energy-bar" style="background: rgba(255,255,255,0.1); border-radius: 4px; height: 8px; width: 100%; position: relative; overflow: hidden;">
                          <div class="battle-energy-fill" style="background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%); height: 100%; width: ${energyPercent}%; transition: width 0.5s ease; border-radius: 4px;"></div>
                      </div>
                      <span style="font-weight:600; font-size: 0.9em; min-width: 50px; text-align: right;">${energyInfo}/${maxEnergyInfo}</span>
                  </div>
              </div>
              ${highlightText}
          </div>
      `;
    })
    .join('');
}

export function renderBattlePlaybackLog(state) {
  const logDiv = document.getElementById('battle-playback-log');
  if (!logDiv) return;
  const timeline = state.timeline;
  const step = Math.min(Math.max(state.currentStep, 0), timeline.length - 1);
  const current = timeline[step];
  let html = current ? current.description : '—';
  const statusSnapshot = state.statusTimeline?.[step]?.statuses || null;
  const round = current?.entry?.round ?? state.statusTimeline?.[step]?.round ?? null;
  if (statusSnapshot) {
    const rows = (state.panels || [])
      .map(panel => {
        const key = String(panel.id);
        const list = statusSnapshot[key];
        if (!list || !list.length) {
          return '';
        }
        // Используем улучшенную систему пресетов (Stage 4)
        const badges = createGroupedStatusBadgesHtml(list, {
          showTooltip: true,
          showStack: true,
          showDuration: false,
          currentRound: round || null
        });
        const name = escapeHtml(panel.name || `#${panel.id}`);
        return `<div class=\"battle-status-row\"><div class=\"battle-status-row-label\">${name}</div><div class=\"battle-status-row-badges\">${badges}</div></div>`;
      })
      .filter(Boolean)
      .join('');
    if (rows) {
      html += `<div class=\"battle-status-summary\">${rows}</div>`;
    }
  }
  if (round != null) {
    const telemetryMessages = state.telemetryByRound?.[round];
    if (Array.isArray(telemetryMessages) && telemetryMessages.length) {
      const eventsHtml = telemetryMessages
        .map(message => `<div class="battle-log-event">${escapeHtml(message)}</div>`)
        .join('');
      html += `<div class="battle-log-effects">${eventsHtml}</div>`;
    }
  }
  logDiv.innerHTML = html;
}

export function renderBattlePlaybackControls(state) {
  const container = document.getElementById('battle-playback');
  if (!container) return;
  if (!state.timeline.length) {
    container.classList.remove('active');
    return;
  }
  container.classList.add('active');
  const slider = document.getElementById('battle-playback-slider');
  const label = document.getElementById('battle-playback-progress-label');
  const playBtn = document.getElementById('battle-playback-play');
  const maxIndex = Math.max(state.timeline.length - 1, 0);
  const currentStep = Math.min(Math.max(state.currentStep, 0), maxIndex);
  
  if (slider) {
    slider.max = String(maxIndex);
    slider.value = String(currentStep);
    // Добавляем подсказку при наведении
    const currentEntry = state.timeline[currentStep];
    const round = currentEntry?.entry?.round ?? null;
    const eventType = currentEntry?.entry?.event ?? 'unknown';
    slider.title = `Шаг ${currentStep + 1} из ${maxIndex + 1}${round != null ? ` • Раунд ${round}` : ''} • ${eventType}`;
  }
  
  if (label) {
    const percent = maxIndex > 0 ? Math.round((currentStep / maxIndex) * 100) : 0;
    label.textContent = `${currentStep + 1} / ${maxIndex + 1} (${percent}%)`;
    label.title = `Прогресс: ${percent}%`;
  }
  
  if (playBtn) {
    playBtn.textContent = state.isPlaying ? '⏸' : '▶️';
    playBtn.title = state.isPlaying ? 'Пауза' : 'Воспроизвести';
  }
}

export function renderBattlePlaybackStage(state) {
  const stage = document.getElementById('battle-playback-stage');
  if (!stage) return;
  const panels = state.panels || [];
  if (!panels.length) {
    stage.innerHTML = `
        <div class="battle-stage-avatar battle-stage-avatar--team-a" data-team="A" data-panel-id="">
            <div class="battle-stage-avatar-inner">
                <div class="battle-stage-initials">A</div>
            </div>
            <div class="battle-stage-label">
                <div class="battle-stage-owner">Команда A</div>
                <div class="battle-stage-name">—</div>
            </div>
        </div>
        <div class="battle-stage-center" id="battle-stage-center">The Stack</div>
        <div class="battle-stage-avatar battle-stage-avatar--team-b" data-team="B" data-panel-id="">
            <div class="battle-stage-avatar-inner">
                <div class="battle-stage-initials">B</div>
            </div>
            <div class="battle-stage-label">
                <div class="battle-stage-owner">Команда B</div>
                <div class="battle-stage-name">—</div>
            </div>
        </div>
    `;
    return;
  }

  const arenaRaw = state.battle?.arena || 'THE_STACK';
  const arenaLabel = arenaRaw === 'THE_STACK' ? 'The Stack' : arenaRaw;
  const teamA = getPanelByTeam(panels, 'A') || panels[0] || null;
  const teamB = getPanelByTeam(panels, 'B') || panels[1] || null;

  const renderStageAvatar = (panel, team) => {
    const safeTeam = team || 'A';
    const name = panel?.name || '—';
    const initials = name ? name.charAt(0).toUpperCase() : '?';
    const avatar = panel?.avatar;
    const panelId = panel?.id ?? '';
    const owner = panel?.owner || `Команда ${safeTeam}`;
    const summonDisplay = resolveSummonDisplay(panel, panels);
    const summonBadge = summonDisplay
      ? `<span class="battle-stage-badge" title="${escapeHtml(summonDisplay.tooltip)}">${escapeHtml(summonDisplay.badgeLabel)}</span>`
      : '';
    const extraClass = panel?.isSummon ? ' battle-stage-avatar--summon' : '';
    return `
        <div class="battle-stage-avatar battle-stage-avatar--team-${safeTeam.toLowerCase()}${extraClass}" data-team="${safeTeam.toUpperCase()}" data-panel-id="${panelId}">
            <div class="battle-stage-avatar-inner">
                ${avatar ? `<img src="${escapeHtml(avatar)}" alt="" onerror="this.onerror=null;this.remove();">` : `<div class="battle-stage-initials">${escapeHtml(initials)}</div>`}
            </div>
            <div class="battle-stage-label">
                <div class="battle-stage-owner">${escapeHtml(owner)}</div>
                <div class="battle-stage-name">${escapeHtml(name)}${summonBadge ? ` ${summonBadge}` : ''}</div>
            </div>
        </div>
    `;
  };

  stage.innerHTML = `
      ${renderStageAvatar(teamA, 'A')}
      <div class="battle-stage-center" id="battle-stage-center">${escapeHtml(arenaLabel)}</div>
      ${renderStageAvatar(teamB, 'B')}
  `;
}

export function flashStageCenter(duration = 600) {
  const center = document.getElementById('battle-stage-center');
  if (!center) return;
  center.classList.remove('flash');
  void center.offsetWidth;
  center.classList.add('flash');
  if (duration > 0) {
    setTimeout(() => center.classList.remove('flash'), duration);
  }
}

export function setLogFlash() {
  const logDiv = document.getElementById('battle-playback-log');
  if (!logDiv) return;
  logDiv.classList.remove('flash');
  void logDiv.offsetWidth;
  logDiv.classList.add('flash');
  setTimeout(() => logDiv.classList.remove('flash'), 420);
}

export function spawnBattleEffect(type) {
  const layer = document.getElementById('battle-playback-effects');
  if (!layer) return;
  const el = document.createElement('div');
  el.className = `battle-effect-${type}`;
  layer.innerHTML = '';
  layer.appendChild(el);
  const ttl = type === 'evolution' ? 1400 : type === 'stack' ? 1200 : 800;
  setTimeout(() => {
    if (el.parentNode === layer) {
      el.remove();
    }
  }, ttl);
}

export function highlightBattlePanel(panelId, className, duration = 600) {
  if (panelId == null) return;
  const panel = document.querySelector(`.battle-combatant-panel[data-panel-id="${panelId}"]`);
  if (!panel) return;
  panel.classList.remove(className);
  void panel.offsetWidth;
  panel.classList.add(className);
  if (duration > 0) {
    setTimeout(() => panel.classList.remove(className), duration);
  }
}

export function highlightStageAvatar(team, className, duration = 600) {
  if (!team) return;
  const node = document.querySelector(`.battle-stage-avatar[data-team="${String(team).toUpperCase()}"]`);
  if (!node) return;
  node.classList.remove(className);
  void node.offsetWidth;
  node.classList.add(className);
  if (duration > 0) {
    setTimeout(() => node.classList.remove(className), duration);
  }
}

export function spawnPanelDamage(panelId, value, isCrit = false) {
  if (value == null || Math.round(value) <= 0) return;
  if (panelId == null) return;
  const panel = document.querySelector(`.battle-combatant-panel[data-panel-id="${panelId}"]`);
  if (!panel) return;
  const el = document.createElement('div');
  el.className = `battle-damage${isCrit ? ' battle-damage--crit' : ''}`;
  el.textContent = `-${Math.max(0, Math.round(value))}`;
  panel.appendChild(el);
  const ttl = isCrit ? 1000 : 850;
  setTimeout(() => {
    el.remove();
  }, ttl);
}

export function spawnStageDamage(team, value, isCrit = false) {
  if (value == null || Math.round(value) <= 0) return;
  if (!team) return;
  const node = document.querySelector(`.battle-stage-avatar[data-team="${String(team).toUpperCase()}"]`);
  if (!node) return;
  const el = document.createElement('div');
  el.className = `battle-damage${isCrit ? ' battle-damage--crit' : ''}`;
  el.textContent = `-${Math.max(0, Math.round(value))}`;
  node.appendChild(el);
  const ttl = isCrit ? 1100 : 900;
  setTimeout(() => {
    el.remove();
  }, ttl);
}
